import React, { useState, useEffect } from 'react';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const publicFetch = async (url) => {
  const res = await fetch(`${API_URL}${url}`);
  if (!res.ok) throw new Error('Yükleme hatası');
  return res.json();
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// Saat 00:00 değilse saati de göster
const formatSaleDate = (dateStr) => {
  const date = new Date(dateStr);
  const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
  return date.toLocaleDateString('tr-TR', {
    day: 'numeric', month: 'long', year: 'numeric',
    ...(hasTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

// Satış başlangıcına kalan süreyi hesapla
const calcSaleRemaining = (targetDate) => {
  const diff = new Date(targetDate) - new Date();
  if (diff <= 0) return null;
  const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours   = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
};

const getTimeRemaining = (dateStr) => {
  const now = new Date();
  const target = new Date(dateStr);
  const diffMs = target - now;
  if (diffMs <= 0) return null;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (diffDays > 30) return `${Math.floor(diffDays / 30)} ay sonra`;
  if (diffDays > 0) return `${diffDays} gün sonra`;
  if (diffHours > 0) return `${diffHours} saat sonra`;
  return 'Bugün';
};

const getSaleStatus = (event) => {
  const now = new Date();
  if (event.saleStartDate && new Date(event.saleStartDate) > now) return 'not_started';
  if (event.saleEndDate && new Date(event.saleEndDate) < now) return 'closed';
  return 'open';
};

const isReservationType = (event) => parseInt(event.eventType) === 1;

const getEventImage = (event) => {
  const photo = event.displayPhoto || event.mainPhoto || event.venueMainPhoto;
  if (photo) return `${API_URL}${photo}`;
  return null;
};

const EventCard = ({ event, onSelect, index }) => {
  const saleStatus = getSaleStatus(event);
  const isRes = isReservationType(event);
  const timeRemaining = getTimeRemaining(event.date);

  const allPhotos = React.useMemo(() => {
    const photos = [];
    if (event.mainPhoto) photos.push(`${API_URL}${event.mainPhoto}`);
    if (event.galleryPhotos?.length) event.galleryPhotos.forEach(p => photos.push(`${API_URL}${p}`));
    if (!photos.length && event.venueMainPhoto) photos.push(`${API_URL}${event.venueMainPhoto}`);
    return photos;
  }, [event]);

  const [photoIdx, setPhotoIdx] = React.useState(0);

  // Satış başlangıcına geri sayım
  const [saleRemaining, setSaleRemaining] = React.useState(
    saleStatus === 'not_started' ? calcSaleRemaining(event.saleStartDate) : null
  );
  // Süre dolunca butonu aktifleştirmek için local state
  const [saleJustOpened, setSaleJustOpened] = React.useState(false);

  React.useEffect(() => {
    if (saleStatus !== 'not_started' || !event.saleStartDate) return;
    const interval = setInterval(() => {
      const r = calcSaleRemaining(event.saleStartDate);
      setSaleRemaining(r);
      if (!r) {
        setSaleJustOpened(true); // satış başladı, butonu aktifleştir
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [saleStatus, event.saleStartDate]);

  const prevPhoto = (e) => { e.stopPropagation(); setPhotoIdx(i => (i - 1 + allPhotos.length) % allPhotos.length); };
  const nextPhoto = (e) => { e.stopPropagation(); setPhotoIdx(i => (i + 1) % allPhotos.length); };

  return (
    <div
      className="hp-event-card"
      style={{ animationDelay: `${index * 80}ms` }}
      onClick={() => saleStatus === 'open' && onSelect(event)}
    >
      <div className="hp-card-image">
        {allPhotos.length > 0 ? (
          <>
            <img src={allPhotos[photoIdx]} alt={event.name} />
            {allPhotos.length > 1 && (
              <>
                <button className="hp-carousel-btn hp-carousel-prev" onClick={prevPhoto}>‹</button>
                <button className="hp-carousel-btn hp-carousel-next" onClick={nextPhoto}>›</button>
                <div className="hp-carousel-dots">
                  {allPhotos.map((_, i) => (
                    <span key={i} className={`hp-carousel-dot ${i === photoIdx ? 'active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setPhotoIdx(i); }} />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="hp-card-image-placeholder">
            <i className={`fa-solid ${isRes ? 'fa-clipboard-list' : 'fa-masks-theater'}`} />
          </div>
        )}
        <div className={`hp-card-badge ${isRes ? 'badge-res' : 'badge-ticket'}`}>
          {isRes ? 'Rezervasyon' : 'Bilet Satışı'}
        </div>
        {saleStatus !== 'open' && (
          <div className="hp-card-overlay">
            {saleStatus === 'not_started'
              ? `Satış ${formatSaleDate(event.saleStartDate)} başlıyor`
              : 'Satışlar Kapandı'}
          </div>
        )}
      </div>

      <div className="hp-card-body">
        <h3 className="hp-card-title">{event.name}</h3>

        {event.venueName && (
          <div className="hp-card-meta">
            <i className="fa-solid fa-location-dot hp-meta-icon" />
            <span>{event.venueName}</span>
          </div>
        )}

        <div className="hp-card-meta">
          <i className="fa-solid fa-calendar hp-meta-icon" />
          <span>{formatDate(event.date)}</span>
        </div>

        {timeRemaining && (
          <div className="hp-card-countdown">
            <span className="hp-countdown-dot" />
            {timeRemaining}
          </div>
        )}

        {event.description && <p className="hp-card-desc">{event.description}</p>}

        <button
          className={`hp-card-btn ${(saleStatus !== 'open' && !saleJustOpened) ? 'hp-card-btn--disabled' : ''}`}
          disabled={saleStatus !== 'open' && !saleJustOpened}
        >
          {saleStatus === 'closed' ? (
            <><i className="fa-solid fa-lock" /> Satışlar Kapandı</>
          ) : (saleStatus === 'not_started' && !saleJustOpened) ? (
            <><i className="fa-solid fa-clock" /> Yakında</>
          ) : isRes ? (
            <><i className="fa-solid fa-key" /> Rezervasyon Yap</>
          ) : (
            <><i className="fa-solid fa-ticket" /> Bilet Al</>
          )}
        </button>

        {/* Yakında butonunun altında silik geri sayım */}
        {saleStatus === 'not_started' && !saleJustOpened && saleRemaining && (
          <div className="hp-sale-countdown">
            {saleRemaining.days > 0 && (
              <span>{saleRemaining.days} gün</span>
            )}
            {(saleRemaining.days > 0 || saleRemaining.hours > 0) && (
              <span>{String(saleRemaining.hours).padStart(2,'0')} saat</span>
            )}
            {(saleRemaining.days === 0) && (
              <span>{String(saleRemaining.minutes).padStart(2,'0')} dk</span>
            )}
            {(saleRemaining.days === 0 && saleRemaining.hours === 0) && (
              <span>{String(saleRemaining.seconds).padStart(2,'0')} sn</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const HomePage = ({
  onEventSelect, onCodeAccess, onGoToLogin, onGoToRegister,
  isLoggedIn, user, onLogout, onGoToProfile,
  onGoToEventManagement, onGoToAdminPanel, onGoToQrScanner, onGoToVenueManage,
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);

  const role = user?.role;
  const isAdmin = role === 'Admin';
  const isEventOwner = role === 'EventOwner' || isAdmin;

  useEffect(() => {
    publicFetch('/api/events')
      .then(data => { setEvents(data.filter(e => e.isActive)); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filteredEvents = events.filter(e => {
    if (filter === 'ticket') return !isReservationType(e);
    if (filter === 'reservation') return isReservationType(e);
    return true;
  });

  const handleEventClick = (event) => {
    if (isReservationType(event)) onCodeAccess?.(event);
    else onEventSelect?.(event);
  };

  return (
    <div className="hp-root">
      {/* ── HEADER ── */}
      <header className="hp-header">
        <div className="hp-header-inner">
          <div className="hp-logo">
            <span className="hp-logo-icon">🎭</span>
            <div>
              <div className="hp-logo-name">Gişe Görevlisi</div>
              <div className="hp-logo-sub">Bilet & Rezervasyon</div>
            </div>
          </div>

          <nav className="hp-nav">
            {isLoggedIn ? (
              <div className="hp-nav-logged">
                {isEventOwner && (
                  <div className="hp-user-wrap">
                    <button className="hp-admin-btn"
                      onClick={() => { setShowAdminMenu(m => !m); setShowUserMenu(false); }}>
                      <i className="fa-solid fa-gear" /> Yönetim
                      <span className="hp-chevron"><i className="fa-solid fa-chevron-down" /></span>
                    </button>
                    {showAdminMenu && (
                      <div className="hp-user-menu hp-admin-menu">
                        {isEventOwner && (
                          <button onClick={() => { setShowAdminMenu(false); onGoToEventManagement?.(); }}>
                            <i className="fa-solid fa-calendar-days" /> Etkinlik Yönetimi
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => { setShowAdminMenu(false); onGoToAdminPanel?.(); }}>
                            <i className="fa-solid fa-shield-halved" /> Admin Paneli
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => { setShowAdminMenu(false); onGoToVenueManage?.(); }}>
                            <i className="fa-solid fa-building-columns" /> Salon Yönetimi
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => { setShowAdminMenu(false); onGoToQrScanner?.(); }}>
                            <i className="fa-solid fa-qrcode" /> QR Okuyucu
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="hp-user-wrap">
                  <button className="hp-user-btn"
                    onClick={() => { setShowUserMenu(m => !m); setShowAdminMenu(false); }}>
                    <span className="hp-user-avatar"><i className="fa-solid fa-circle-user" /></span>
                    <span className="hp-user-name">{user?.fullName}</span>
                    <span className="hp-chevron"><i className="fa-solid fa-chevron-down" /></span>
                  </button>
                  {showUserMenu && (
                    <div className="hp-user-menu">
                      <div className="hp-menu-user-info">
                        <div className="hp-menu-username">{user?.fullName}</div>
                        <div className="hp-menu-role">
                          {role === 'Admin'      ? <><i className="fa-solid fa-key" /> Admin</> :
                           role === 'EventOwner' ? <><i className="fa-solid fa-star" /> Etkinlik Sahibi</> :
                                                  <><i className="fa-solid fa-ticket" /> Üye</>}
                        </div>
                      </div>
                      <div className="hp-menu-divider" />
                      <button onClick={() => { setShowUserMenu(false); onGoToProfile?.(); }}>
                        <i className="fa-solid fa-user" /> Profilim
                      </button>
                      <div className="hp-menu-divider" />
                      <button className="hp-menu-logout" onClick={() => { setShowUserMenu(false); onLogout?.(); }}>
                        <i className="fa-solid fa-right-from-bracket" /> Çıkış Yap
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="hp-auth-btns">
                <button className="hp-btn-ghost" onClick={onGoToLogin}>Giriş Yap</button>
                <button className="hp-btn-primary" onClick={onGoToRegister}>Kayıt Ol</button>
              </div>
            )}
          </nav>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="hp-hero">
        <div className="hp-hero-bg" />
        <div className="hp-hero-content">
          <h1 className="hp-hero-title">
            Etkinlikler<br />
            <span className="hp-hero-accent">sizi bekliyor</span>
          </h1>
          <p className="hp-hero-sub">Tiyatro, konser ve daha fazlası için biletinizi hemen alın.</p>
        </div>
        <div className="hp-hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="var(--hp-page-bg)" />
          </svg>
        </div>
      </section>

      {/* ── ETKİNLİKLER ── */}
      <main className="hp-main">
        <div className="hp-filters">
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'ticket', label: 'Bilet Satışı', icon: 'fa-ticket' },
            { key: 'reservation', label: 'Rezervasyon', icon: 'fa-clipboard-list' },
          ].map(f => (
            <button key={f.key}
              className={`hp-filter-btn ${filter === f.key ? 'hp-filter-btn--active' : ''}`}
              onClick={() => setFilter(f.key)}>
              {f.icon && <i className={`fa-solid ${f.icon}`} />} {f.label}
            </button>
          ))}
          <span className="hp-filter-count">{filteredEvents.length} etkinlik</span>
        </div>

        {loading ? (
          <div className="hp-loading">
            <div className="hp-spinner" />
            <p>Etkinlikler yükleniyor...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="hp-empty">
            <div className="hp-empty-icon"><i className="fa-solid fa-masks-theater" /></div>
            <h3>Aktif etkinlik bulunamadı</h3>
            <p>Yakında yeni etkinlikler eklenecek.</p>
          </div>
        ) : (
          <div className="hp-grid">
            {filteredEvents.map((event, i) => (
              <EventCard key={event.id} event={event} onSelect={handleEventClick} index={i} />
            ))}
          </div>
        )}
      </main>

      <footer className="hp-footer">
        <p>© 2026 Gişe Görevlisi · Tüm hakları saklıdır.</p>
      </footer>
    </div>
  );
};

export default HomePage;